import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Edit, Trash2, LogOut, Eye, EyeOff, Sparkles, HelpCircle, FileText, Package, Users, Boxes, MessageCircle } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import InstallAppButton from "../components/InstallAppButton";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "general",
    featured_image: "",
    video_url: "",
    content_type: "blog",
    tags: "",
    published: true,
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/api/blog?published_only=false`, {
        withCredentials: true,
      });
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const postData = {
      ...formData,
      tags: formData.tags.split(",").map(t => t.trim()).filter(t => t),
    };

    try {
      if (editingPost) {
        await axios.put(`${API}/api/blog/${editingPost.id}`, postData, {
          withCredentials: true,
        });
      } else {
        await axios.post(`${API}/api/blog`, postData, {
          withCredentials: true,
        });
      }
      
      setShowForm(false);
      setEditingPost(null);
      setFormData({
        title: "",
        content: "",
        category: "general",
        featured_image: "",
        video_url: "",
        content_type: "blog",
        tags: "",
        published: true,
      });
      fetchPosts();
    } catch (error) {
      console.error("Error saving post:", error);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      category: post.category,
      featured_image: post.featured_image || "",
      video_url: post.video_url || "",
      content_type: post.content_type || "blog",
      tags: post.tags ? post.tags.join(", ") : "",
      published: post.published,
    });
    setShowForm(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await axios.delete(`${API}/api/blog/${postId}`, {
        withCredentials: true,
      });
      fetchPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "fun_fact_friday":
        return <Sparkles className="w-4 h-4" />;
      case "trivia_tuesday":
        return <HelpCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      meet_me_monday: "Meet Me Monday",
      trivia_tuesday: "Trivia Tuesday",
      wondering_wednesday: "Wondering Wednesday",
      think_thursday: "Think Thursday",
      fun_fact_friday: "Fun Fact Friday",
      storytelling_saturday: "Storytelling Saturday",
      simplicity_sunday: "Simplicity Sunday",
      livestock_lesson: "Livestock Lesson",
      general: "General"
    };
    return labels[category] || "General";
  };

  const getCategoryColor = (category) => {
    const colors = {
      meet_me_monday: "bg-[#E63946] text-white",
      trivia_tuesday: "bg-[#457B9D] text-white",
      wondering_wednesday: "bg-[#2A9D8F] text-white",
      think_thursday: "bg-[#9B59B6] text-white",
      fun_fact_friday: "bg-[#E9C46A] text-[#1D3557]",
      storytelling_saturday: "bg-[#E76F51] text-white",
      simplicity_sunday: "bg-[#84A98C] text-white",
      livestock_lesson: "bg-[#8B4513] text-white",
      general: "bg-[#1D3557] text-white"
    };
    return colors[category] || colors.general;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <InstallAppButton />
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-black text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Admin Dashboard
            </h1>
            <p className="text-[#1D3557] font-medium">
              Welcome back, {user?.name || "Admin"}!
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link
              to="/admin/orders"
              className="btn-teal text-base py-3 px-6 inline-flex items-center gap-2"
              data-testid="view-orders-btn"
            >
              <Package className="w-5 h-5" />
              View Orders
            </Link>
            <Link
              to="/admin/inventory"
              className="bg-[#E9C46A] hover:bg-[#1D3557] hover:text-white text-[#1D3557] font-bold py-3 px-6 rounded-2xl border-3 border-[#1D3557] inline-flex items-center gap-2 transition-colors"
              style={{ boxShadow: "3px 3px 0 0 rgba(29,53,87,0.4)" }}
              data-testid="view-inventory-btn"
            >
              <Boxes className="w-5 h-5" />
              Inventory
            </Link>
            <Link
              to="/admin/testimonials"
              className="bg-[#457B9D] hover:bg-[#1D3557] text-white font-bold py-3 px-6 rounded-2xl border-3 border-[#1D3557] inline-flex items-center gap-2 transition-colors"
              style={{ boxShadow: "3px 3px 0 0 rgba(29,53,87,0.4)" }}
              data-testid="view-testimonials-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Reviews
            </Link>
            <Link
              to="/admin/barbados-reservations"
              className="bg-[#E63946] hover:bg-[#1D3557] text-white font-bold py-3 px-6 rounded-2xl border-3 border-[#1D3557] inline-flex items-center gap-2 transition-colors"
              style={{ boxShadow: "3px 3px 0 0 rgba(29,53,87,0.4)" }}
              data-testid="view-reservations-btn"
            >
              <Users className="w-5 h-5" />
              🇧🇧 Reservations
            </Link>
            <button
              onClick={() => {
                setEditingPost(null);
                setFormData({
                  title: "",
                  content: "",
                  category: "general",
                  featured_image: "",
                  video_url: "",
                  content_type: "blog",
                  tags: "",
                  published: true,
                });
                setShowForm(true);
              }}
              className="btn-primary text-base py-3 px-6 inline-flex items-center gap-2"
              data-testid="new-post-btn"
            >
              <Plus className="w-5 h-5" />
              New Post
            </button>
            <button
              onClick={handleLogout}
              className="btn-secondary text-base py-3 px-6 inline-flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="tactile-card p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-2xl font-bold text-[#1D3557] mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                {editingPost ? "Edit Post" : "Create New Post"}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input-field"
                    placeholder="Enter post title..."
                    required
                    data-testid="post-title-input"
                  />
                </div>

                <div>
                  <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="select-field"
                    data-testid="post-category-select"
                  >
                    <option value="general">General</option>
                    <option value="meet_me_monday">Meet Me Monday</option>
                    <option value="trivia_tuesday">Trivia Tuesday</option>
                    <option value="wondering_wednesday">Wondering Wednesday</option>
                    <option value="think_thursday">Think About It Thursday</option>
                    <option value="fun_fact_friday">Fun Fact Friday</option>
                    <option value="storytelling_saturday">Storytelling Saturday</option>
                    <option value="simplicity_sunday">Simplicity Sunday</option>
                    <option value="livestock_lesson">Livestock Lesson</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Content Type
                  </label>
                  <select
                    value={formData.content_type || "blog"}
                    onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                    className="select-field"
                    data-testid="post-content-type-select"
                  >
                    <option value="blog">Blog Post</option>
                    <option value="video">Video</option>
                    <option value="snippet">Quick Tip / Snippet</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Content
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="textarea-field"
                    placeholder="Write your post content..."
                    required
                    data-testid="post-content-textarea"
                  />
                </div>

                <div>
                  <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Featured Image URL
                  </label>
                  <input
                    type="url"
                    value={formData.featured_image}
                    onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                    className="input-field"
                    placeholder="https://example.com/image.jpg"
                    data-testid="post-image-input"
                  />
                </div>

                {formData.content_type === "video" && (
                  <div>
                    <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                      Video URL (YouTube, Vimeo, etc.)
                    </label>
                    <input
                      type="url"
                      value={formData.video_url || ""}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      className="input-field"
                      placeholder="https://youtube.com/watch?v=..."
                      data-testid="post-video-input"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    className="input-field"
                    placeholder="farm, animals, detective"
                    data-testid="post-tags-input"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-5 h-5"
                    data-testid="post-published-checkbox"
                  />
                  <label htmlFor="published" className="text-[#1D3557] font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Publish immediately
                  </label>
                </div>

                <div className="flex gap-4 pt-4">
                  <button type="submit" className="btn-primary flex-1" data-testid="save-post-btn">
                    {editingPost ? "Update Post" : "Create Post"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPost(null);
                    }}
                    className="btn-secondary flex-1"
                    data-testid="cancel-post-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* Posts Table */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="tactile-card p-12 text-center">
            <h3 className="text-2xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              No posts yet!
            </h3>
            <p className="text-[#1D3557] font-medium mb-6">
              Create your first blog post to get started.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="create-first-post-btn"
            >
              <Plus className="w-5 h-5" />
              Create First Post
            </button>
          </div>
        ) : (
          <div className="tactile-card overflow-hidden" data-testid="posts-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1D3557] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Title</th>
                    <th className="px-6 py-4 text-left font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Category</th>
                    <th className="px-6 py-4 text-left font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Status</th>
                    <th className="px-6 py-4 text-left font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Date</th>
                    <th className="px-6 py-4 text-right font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y-4 divide-[#FDFBF7]">
                  {posts.map((post) => (
                    <tr key={post.id} className="bg-white hover:bg-[#FDFBF7] transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-bold text-[#1D3557]">{post.title}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold border-2 border-[#1D3557] ${getCategoryColor(post.category)}`}>
                          {getCategoryIcon(post.category)}
                          {getCategoryLabel(post.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {post.published ? (
                          <span className="inline-flex items-center gap-1 text-[#2A9D8F] font-bold">
                            <Eye className="w-4 h-4" />
                            Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#1D3557]/50 font-bold">
                            <EyeOff className="w-4 h-4" />
                            Draft
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[#1D3557] font-medium">
                        {new Date(post.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(post)}
                            className="p-2 bg-[#E9C46A] text-[#1D3557] rounded-lg border-2 border-[#1D3557] hover:shadow-[2px_2px_0px_0px_rgba(29,53,87,1)] transition-all"
                            data-testid={`edit-post-${post.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 bg-[#E63946] text-white rounded-lg border-2 border-[#1D3557] hover:shadow-[2px_2px_0px_0px_rgba(29,53,87,1)] transition-all"
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
